/**
 * Конфигурация навигации сайдбара CRM.
 *
 * Вынесена из AppSidebar.vue: чистая функция от прав и счётчиков, поэтому
 * структуру меню (группы, дубли, видимость по ролям) можно проверять тестом
 * без монтирования компонента.
 */
import {
  HomeIcon,
  UsersIcon,
  BriefcaseIcon,
  BuildingIcon,
  CalendarIcon,
  HandshakeIcon,
  InboxIcon,
  ShieldCheckIcon,
  UserCogIcon,
  TrendingUpIcon,
  BarChartIcon,
  PhoneIcon,
  FileTextIcon,
  SettingsIcon,
  RouteIcon,
  SlidersHorizontalIcon,
} from 'lucide-vue-next'

export interface NavItem {
  /** Отсутствует у групп-аккордеонов: такой пункт только раскрывает подпункты. */
  path?: string
  label: string
  icon?: object
  badge?: string | number
  /** alert — бейдж горит красным (есть просрочка, требующая реакции). */
  badgeVariant?: 'alert'
  children?: NavItem[]
}

export interface NavSection {
  id: string
  title?: string
  items: NavItem[]
}

/** Флаги из usePermissions() — уже развёрнутые значения, не ref'ы. */
export interface NavPermissions {
  isProjectManager: boolean
  canAccessDeals: boolean
  canAccessProperties: boolean
  /** Заводить и править проекты — admin и project_manager. Импорт из Drive под ним же. */
  canManageProperties: boolean
  canAccessCalendar: boolean
  canAccessAgents: boolean
  canAccessAgentTickets: boolean
  canModerateCompanyVerifications: boolean
  canManageAgents: boolean
  canAccessReports: boolean
  canAccessRcAnalytics: boolean
  canModerateRcMappings: boolean
  canAccessDocuments: boolean
  canAccessSettings: boolean
  canAccessRoutingRules: boolean
  canConfigurePipeline: boolean
}

export interface NavBadges {
  newClients?: number
  newDeals?: number
  totalProperties?: number
  pendingVerifications?: number
  /** Просроченные заявки модерации: красят бейдж в алерт (DEV-1165). */
  overdueVerifications?: number
}

type Translate = (key: string) => string

const count = (value?: number): string | undefined => (value ? String(value) : undefined)

/** Группа с единственным подпунктом бессмысленна — показываем сам подпункт. */
const group = (item: NavItem): NavItem[] => {
  const children = item.children ?? []
  if (children.length === 0) return []
  const [only] = children
  if (children.length === 1 && only) return [only]
  return [item]
}

const flatten = (items: NavItem[]): NavItem[] =>
  items.flatMap((item) => [item, ...flatten(item.children ?? [])])

/**
 * Активен ровно один пункт — самый специфичный из подходящих. Без этого
 * `/dashboard/agents-admin` подсвечивал заодно `/dashboard/agents`, а
 * `/dashboard/rc-analytics/moderation` — «Аналитику ЖК».
 */
export const resolveActivePath = (items: NavItem[], routePath: string): string | undefined =>
  flatten(items)
    .map((i) => i.path)
    .filter((p): p is string => {
      if (!p) return false
      // «Дашборд» — префикс всех разделов, поэтому активен только на самом себе.
      if (p === '/dashboard') return routePath === p
      return routePath === p || routePath.startsWith(`${p}/`)
    })
    .sort((a, b) => b.length - a.length)[0]

export const buildSidebarNav = (
  p: NavPermissions,
  t: Translate,
  badges: NavBadges = {},
): { sections: NavSection[]; settings: NavItem | null } => {
  // DEV-1021. Менеджер каталога работает только с «Объектами».
  const items: NavItem[] = [
    ...(!p.isProjectManager
      ? [
          { path: '/dashboard', label: t('common.navigation.dashboard'), icon: HomeIcon },
          {
            path: '/dashboard/clients',
            label: t('common.navigation.clients'),
            icon: UsersIcon,
            badge: count(badges.newClients),
          },
        ]
      : []),
    ...(p.canAccessDeals
      ? [
          {
            path: '/dashboard/deals',
            label: t('common.navigation.deals'),
            icon: BriefcaseIcon,
            badge: count(badges.newDeals),
          },
        ]
      : []),
    // DEV-1166. Раздел вместо одного пункта: импорт из Drive — такая же часть
    // работы с проектами, как их список, и искать его в другом месте меню незачем.
    ...(p.canAccessProperties
      ? group({
          label: t('common.navigation.projectsSection'),
          icon: BuildingIcon,
          children: [
            {
              path: '/dashboard/properties',
              label: t('common.navigation.projectsList'),
              badge: count(badges.totalProperties),
            },
            ...(p.canManageProperties
              ? [
                  {
                    path: '/dashboard/properties/import-drive',
                    label: t('common.navigation.projectsImport'),
                  },
                ]
              : []),
          ],
        })
      : []),
    ...(p.canAccessCalendar
      ? [
          {
            path: '/dashboard/calendar',
            label: t('common.navigation.calendar'),
            icon: CalendarIcon,
          },
        ]
      : []),

    // Агенты и партнёры. Реестр (DEV-476), обращения (DEV-436), приём компаний
    // (DEV-795) и назначение кураторов (DEV-437) — один раздел, не четыре.
    ...group({
      label: t('common.navigation.agents'),
      icon: HandshakeIcon,
      children: [
        ...(p.canAccessAgents
          ? [
              {
                path: '/dashboard/agents',
                label: t('common.navigation.agentsRegistry'),
                icon: UsersIcon,
              },
            ]
          : []),
        ...(p.canAccessAgentTickets
          ? [
              {
                path: '/dashboard/agent-tickets',
                label: t('common.navigation.agentTickets'),
                icon: InboxIcon,
              },
            ]
          : []),
        ...(p.canModerateCompanyVerifications
          ? [
              {
                path: '/dashboard/company-verifications',
                label: t('common.navigation.moderation'),
                icon: ShieldCheckIcon,
                // Просрочка перебивает обычный счётчик очереди: модератор должен увидеть
                // проблему из меню, не заходя в раздел (DEV-1165). Просроченной может быть и
                // заявка вне очереди действий (например ai_no_result) — тогда показываем её.
                badge: count(badges.pendingVerifications || badges.overdueVerifications),
                badgeVariant: badges.overdueVerifications ? ('alert' as const) : undefined,
              },
            ]
          : []),
        ...(p.canManageAgents
          ? [
              {
                path: '/dashboard/agents-admin',
                label: t('common.navigation.agentsAdmin'),
                icon: UserCogIcon,
              },
            ]
          : []),
      ],
    }),

    // Аналитика: отчёты + аналитика ЖК (DEV-801) с её модерацией (DEV-824).
    ...group({
      label: t('common.navigation.analytics'),
      icon: TrendingUpIcon,
      children: [
        ...(p.canAccessReports
          ? [
              {
                path: '/dashboard/reports',
                label: t('common.navigation.reports'),
                icon: BarChartIcon,
              },
            ]
          : []),
        ...(p.canAccessRcAnalytics
          ? [
              {
                path: '/dashboard/rc-analytics',
                label: t('common.navigation.rcAnalytics'),
                icon: TrendingUpIcon,
              },
            ]
          : []),
        ...(p.canModerateRcMappings
          ? [
              {
                path: '/dashboard/rc-analytics/moderation',
                label: t('common.navigation.rcModeration'),
                icon: ShieldCheckIcon,
              },
            ]
          : []),
      ],
    }),

    ...(!p.isProjectManager
      ? [{ path: '/dashboard/calls', label: t('common.navigation.calls'), icon: PhoneIcon }]
      : []),
    ...(p.canAccessDocuments
      ? [
          {
            path: '/dashboard/documents',
            label: t('common.navigation.documents'),
            icon: FileTextIcon,
          },
        ]
      : []),
  ]

  // Настройки живут в футере: сама страница + системные конструкторы
  // (маршрутизация DEV-352, воронка DEV-522) как подпункты.
  const settings: NavItem | null = p.canAccessSettings
    ? {
        path: '/dashboard/settings',
        label: t('common.navigation.settings'),
        icon: SettingsIcon,
        children: [
          ...(p.canAccessRoutingRules
            ? [
                {
                  path: '/dashboard/routing-rules',
                  label: t('common.navigation.routingRules'),
                  icon: RouteIcon,
                },
              ]
            : []),
          ...(p.canConfigurePipeline
            ? [
                {
                  path: '/dashboard/pipeline',
                  label: t('common.navigation.pipelineConstructor'),
                  icon: SlidersHorizontalIcon,
                },
              ]
            : []),
        ],
      }
    : null

  if (settings && settings.children?.length === 0) delete settings.children

  return {
    sections: [{ id: 'main', items }].filter((s) => s.items.length > 0),
    settings,
  }
}
